import { ReactNode, useEffect } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  showDragHandle?: boolean;
  maxHeight?: string;
}

/**
 * BottomSheet - Mobile-friendly modal replacement
 *
 * Provides native app-like experience for mobile with:
 * - Slides up from bottom
 * - Drag-to-dismiss gesture
 * - Backdrop with 60% opacity
 * - Smooth spring animations
 * - Safe area support for iOS notch/home indicator
 *
 * @example
 * <BottomSheet
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Select Time Slot"
 * >
 *   <div className="p-4">Content here</div>
 * </BottomSheet>
 */
export const BottomSheet = ({
  isOpen,
  onClose,
  children,
  title,
  showDragHandle = true,
  maxHeight = '90dvh',
}: BottomSheetProps) => {
  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    // Close if dragged down > 100px
    if (info.offset.y > 100) {
      onClose();
    }
  };

  // Focus trap - trap focus within the sheet when open
  useEffect(() => {
    if (!isOpen) return;

    const focusableElements = document.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      if (e.key === 'Tab' && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-ink/55 backdrop-blur-[4px] z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'bottom-sheet-title' : undefined}
            className="fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-3xl shadow-modal overflow-hidden"
            style={{ maxHeight }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300
            }}
            drag="y"
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-line">
              {showDragHandle && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-2">
                  <div className="w-12 h-1 bg-line-strong rounded-pill" aria-hidden="true" />
                </div>
              )}

              {title && (
                <h2
                  id="bottom-sheet-title"
                  className="text-h3 text-ink pt-2"
                >
                  {title}
                </h2>
              )}

              <button
                onClick={onClose}
                className="ml-auto min-w-touch-target min-h-touch-target flex items-center justify-center rounded-md text-soft hover:bg-surface-2 hover:text-ink active:bg-surface-2 transition-colors tap-highlight-transparent"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div
              className="overflow-y-auto touch-pan-y"
              style={{
                maxHeight: `calc(${maxHeight} - 64px)`,
                paddingBottom: 'env(safe-area-inset-bottom)'
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Only render on client side
  if (typeof window === 'undefined') return null;

  return createPortal(content, document.body);
};
